import * as React from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import { visuallyHidden } from '@mui/utils';
import { Button, Dialog, DialogActions, DialogContent, Divider, Grid, LinearProgress, Switch, TextField, IconButton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { ROWS_PER_PAGE } from '../../utils/constant';
import { formatDate, decryptValue } from '../../utils/helper';
import Confirmation from '../Common/Confirmation';
import { fetchRedditAC, createRedditAC, updateRedditAC, deleteRedditAC, toggleActivationRedditAC } from '../../store/cookieSlice';
import RedditForm from '../reddit/RedditForm';
import { NoData } from '../Common/NoData';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import * as Yup from "yup";
import { useFormik } from 'formik';
import PasswordVisibility from '../Common/PasswordVisibility';
import { toast } from 'react-toastify';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CryptoJS from "crypto-js";
import { Visibility, VisibilityOff } from '@mui/icons-material'
import MaskedValue from '../Common/MaskedValue';

const RedditActions = ({ page, setPage, getReddits, reddit, setSelecteReddit, setRedditModal, canDelete, canEdit, canAdd }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const dispatch = useDispatch();
    const handleDelete = async (id) => {
        try {
            dispatch(deleteRedditAC(id)).then(() => {
                getReddits();
            });
        } catch (error) {
            console.error(error);
        }
    };
    return (
        <Box gap={2} display='flex' className="table-margin">
            <EditOutlinedIcon
                color='primary'
                className={`${!canEdit && 'icon-disabled'} edit-color`}
                onClick={() => {
                    if (canEdit) {
                        setSelecteReddit(reddit);
                        setRedditModal(true);
                    }
                }}
                style={{ cursor: canEdit ? 'pointer' : 'not-allowed' }}
            />
            <DeleteOutlineOutlinedIcon className={`${!canDelete && 'icon-disabled'} danger-color`} onClick={() => {
                if (canDelete) {
                    setIsOpen(true);
                }
            }} />
            <Confirmation open={isOpen} setOpen={setIsOpen} msg={"Are you sure you want to remove this Account"} yesClick={() => { handleDelete(reddit.uuid); setIsOpen(false); }} />
        </Box>
    );
};

const headCells = [
    { label: 'App Name', numeric: false, disablePadding: false, id: 'app_name', sortable: true },
    { label: 'Client Id', numeric: false, disablePadding: false, id: 'client_id', sortable: false },
    { label: 'Client Secret', numeric: false, disablePadding: false, id: 'client_secret', sortable: false },
    { label: 'Active', numeric: false, disablePadding: false, id: 'active', sortable: false },
    { label: 'Actions', numeric: false, disablePadding: false, id: 'actions', sortable: false },
];

function EnhancedTableHead(props) {
    const { setOrdering, orderingState, setOrderingState } =
        props;
    const createSortHandler = (property) => (event) => {
        const orderData = orderingState
        const exists = orderData?.find((item) => item.id === property);
        const updated = (() => {

            if (exists) {
                return orderData.map((item) =>
                    item.id === property
                        ? { ...item, order: item.order === "asc" ? "desc" : "asc" }
                        : item
                );
            } else {
                return [...orderData, { id: property, order: "asc" }];
            }
        })();
        setOrderingState(updated)

    };


    React.useEffect(() => {
        const ordering = orderingState.map((item) => {
            return item.order === "asc" ? item.id : "-" + item.id;
        });
        if (ordering?.length) {
            setOrdering(ordering.join(","));
        }
        console.log("orderingState", orderingState);

    }, [orderingState]);

    const removeFilter = (property) => {
        const data = orderingState.filter((item) => item.id !== property)
        setOrderingState(data)
    }
    return (
        <TableHead>
            <TableRow>

                {headCells.map((headCell) => {
                    const key = headCell.id;
                    const sortObj = orderingState.find((item) => item.id === key);
                    const isSorted = !!sortObj;
                    const sortDirection = sortObj?.order || "asc";

                    return (<TableCell
                        className="font-weight"
                        key={key}
                        align={headCell.numeric ? "left" : "left"}
                        padding={headCell.disablePadding ? "none" : "normal"}
                        sortDirection={isSorted ? sortDirection : false}
                    >
                        {headCell.sortable ? <TableSortLabel
                            active={isSorted}
                            direction={sortDirection}
                            onClick={createSortHandler(key)}
                            color="secondary"
                            hideSortIcon={false}
                        >
                            {headCell.label}
                            {isSorted && (
                                <Box component="span" sx={visuallyHidden}>
                                    {sortDirection === "desc" ? "sorted descending" : "sorted ascending"}
                                </Box>
                            )}
                            {isSorted && <FilterListOffIcon onClick={(e) => { e.stopPropagation(); removeFilter(headCell.id) }} className="ml-2" size="small" color='primary' />}
                        </TableSortLabel> : headCell.label}
                    </TableCell>)
                })}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};
function EnhancedTableToolbar(props) {
    const { numSelected, redditModal, page, setPage, getReddits, setRedditModal, setSelecteReddit, selecteReddit, setResetTrigger, resetTrigger } = props;
    return (
        <>
            <RedditForm {...{ page, setPage, getReddits, redditModal, setRedditModal, setSelecteReddit, selecteReddit, resetTrigger }} title={selecteReddit ? "Edit" : "Create"} />
            <Toolbar className='main-list-toolbar'>
                <Typography className="d-flex flex-grow-1 flex-shrink-1 flex-fill" variant="h6" id="tableTitle" component="div">
                    Reddit Accounts
                </Typography>
                <Tooltip title="Create">
                    <Button variant="contained" onClick={() => {
                        setResetTrigger(!resetTrigger);
                        setSelecteReddit(null);
                        setRedditModal(true);
                    }}>Add Account</Button>
                </Tooltip>
            </Toolbar>
        </>
    );
}

EnhancedTableToolbar.propTypes = {
    numSelected: PropTypes.number.isRequired,
};

export default function RedditManage() {
    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState('app_name');
    const [selected, setSelected] = React.useState([]);
    const [rows, setRows] = React.useState([]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(ROWS_PER_PAGE);
    const [redditModal, setRedditModal] = React.useState(false);
    const [selecteReddit, setSelecteReddit] = React.useState(null);
    const [resetTrigger, setResetTrigger] = React.useState(false);
    const [queryString, setQueryString] = React.useState("");
    const [openActiveConfirm, setOpenActiveConfirm] = React.useState(false);
    const reddits = useSelector((state) => state.cookieReducer.redditAC);
    const loading = useSelector((state) => state.cookieReducer.loading);
    const redditCount = useSelector((state) => state.cookieReducer.count);
    const userPermission = useSelector((state) => state.userReducer.userPermission);
    const redditPermissions = userPermission.find((perm) => perm.model_name === "platform")?.permissions || [];
    let canEdit = redditPermissions.includes("change");
    let canDelete = redditPermissions.includes("delete");
    let canAdd = redditPermissions.includes("add");
    const [orderingState, setOrderingState] = React.useState([]);
    const [ordering, setOrdering] = React.useState('-updated_at');
    const dispatch = useDispatch();
    const [showClientSecret, setShowClientSecret] = React.useState({});
    const [showClientId, setShowClientId] = React.useState({});

    React.useEffect(() => {
        setQueryString(`?page=${page + 1}&page_size=${rowsPerPage}&ordering=${ordering}`);
    }, [ordering, page, rowsPerPage]);

    const getReddits = React.useCallback(() => {
        dispatch(fetchRedditAC(queryString));
    }, [dispatch, queryString]);

    React.useEffect(() => {
        if (queryString) {
            getReddits();
        }
    }, [queryString, getReddits]);

    React.useEffect(() => {
        const rowData = reddits?.map((ele) => {
            return {
                id: ele.id,
                app_name: ele.app_name,
                client_id: ele.client_id,
                client_secret: ele.client_secret,
                active: <Switch disabled={loading} className={`${!canEdit && 'icon-disabled'}`} checked={ele.deactivated_on ? false : true} onClick={() => {
                    if (canEdit) {
                        setSelecteReddit(ele);
                        if (!ele?.deactivated_on) {
                            setOpenActiveConfirm(true);
                        } else {
                            dispatch(toggleActivationRedditAC(ele.uuid));
                        }
                    }
                }} />,
                actions: <RedditActions {...{ page, setPage, getReddits, redditModal, canAdd, setRedditModal, setSelecteReddit, canDelete, canEdit }} reddit={ele} />
            };
        });
        setRows(rowData);
    }, [reddits, loading, canEdit, canAdd, canDelete, page, setPage, getReddits, redditModal, setRedditModal, setSelecteReddit, dispatch]);

    function Row(props) {
        const { row, labelId } = props;
        console.log("row.client_secret", decryptValue(row.client_secret));

        return (
            <TableRow hover role="checkbox" tabIndex={-1} key={row.id} id={labelId} className='cursor-pointer'>
                <TableCell align="left">{row.app_name}</TableCell>
                <TableCell align="left">
                    <MaskedValue
                        value={row.client_id}
                        maskLength={40}
                    />
                </TableCell>
                <TableCell align="left">
                    <MaskedValue
                        value={row.client_secret}
                        maskLength={40}
                    />
                </TableCell>
                <TableCell align="left">{row.active}</TableCell>
                <TableCell align="left">{row.actions}</TableCell>
            </TableRow>
        );
    }

    return (
        <Box className='w-100'>
            <Confirmation open={openActiveConfirm} setOpen={setOpenActiveConfirm} msg={"Are you sure you want to inactive this account"} yesClick={() => { dispatch(toggleActivationRedditAC(selecteReddit.uuid)); setOpenActiveConfirm(false); }} />
            <Paper className="w-100 mb-2" >
                <EnhancedTableToolbar numSelected={selected.length} {...{ page, setPage, getReddits, redditModal, setRedditModal, setSelecteReddit, selecteReddit, setResetTrigger, resetTrigger }} />
                {loading && <LinearProgress />}
                <TableContainer className='position-relative'>
                    <Table className='min-width-750' aria-labelledby="tableTitle" size={'small'}>
                        <EnhancedTableHead
                            numSelected={selected.length}
                            orderingState={orderingState}
                            setOrderingState={setOrderingState}
                            setOrdering={setOrdering}
                            onSelectAllClick={() => { }}
                            onRequestSort={() => { }}
                            rowCount={rows?.length}
                        />
                        <TableBody>
                            {rows?.map((row, index) => {
                                const labelId = `enhanced-table-checkbox-${index}`;
                                return <Row row={row} labelId={labelId} key={row.id} />;
                            })}
                        </TableBody>
                    </Table>
                    {!reddits?.length && !loading && <NoData />}
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={redditCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Paper>
        </Box>
    );
}
